import { useMemo } from "react";
import { Button, Flex, IconButton, Text } from "@chakra-ui/react";
import { FiChevronLeft, FiChevronRight } from "react-icons/fi";
import { BREAKPOINTS } from "@lubbezposrednio-frontend/core";

const START_PAGE = 1;
const MAX_VISIBLE_ITEMS_QTY = window.innerWidth < BREAKPOINTS.MOBILE ? 3 : 5;
const MAX_OFFSET = Math.floor(MAX_VISIBLE_ITEMS_QTY / 2);

type CustomPaginationProps = {
  size: number;
  currentPage: number;
  changePage: (page: number) => void;
};

const PageButton: React.FC<{ page: number; active: boolean; onClick: () => void }> = ({ page, active, onClick }) => (
  <Button
    onClick={onClick}
    h="36px"
    minW="36px"
    px="8px"
    borderRadius="10px"
    fontSize="14px"
    fontWeight="700"
    bg={active ? "primary" : "white"}
    color={active ? "white" : "gray.600"}
    border="1px solid"
    borderColor={active ? "primary" : "rgba(67,0,152,0.14)"}
    _hover={{ bg: active ? "#36007a" : "rgba(67,0,152,0.05)", borderColor: active ? "#36007a" : "rgba(67,0,152,0.25)" }}
  >
    {page}
  </Button>
);

const Ellipsis: React.FC = () => (
  <Text color="gray.400" fontWeight="700" px="4px" userSelect="none">
    …
  </Text>
);

export const CustomPagination: React.FC<CustomPaginationProps> = ({ size, currentPage, changePage }) => {
  const visiblePages = useMemo(
    () =>
      new Array(MAX_VISIBLE_ITEMS_QTY)
        .fill(1)
        .map((_, index) => currentPage - MAX_OFFSET + index)
        .filter(page => page > START_PAGE && page < size),
    [currentPage, size]
  );

  if (size <= 1) return null;

  const arrowProps = {
    h: "36px",
    minW: "36px",
    borderRadius: "10px",
    bg: "white",
    color: "primary",
    border: "1px solid rgba(67,0,152,0.14)",
    _hover: { bg: "rgba(67,0,152,0.05)", borderColor: "rgba(67,0,152,0.25)" },
    _disabled: { opacity: 0.4, cursor: "not-allowed", _hover: { bg: "white" } }
  } as const;

  return (
    <Flex justify="center" align="center" gap="6px" mt="28px" pb="8px" wrap="wrap">
      <IconButton aria-label="Poprzednia strona" icon={<FiChevronLeft />} isDisabled={currentPage === START_PAGE} onClick={() => changePage(currentPage - 1)} {...arrowProps} />

      <PageButton page={START_PAGE} active={START_PAGE === currentPage} onClick={() => changePage(START_PAGE)} />

      {START_PAGE + (MAX_OFFSET + 1) < currentPage && <Ellipsis />}

      {visiblePages.map(page => (
        <PageButton key={page} page={page} active={page === currentPage} onClick={() => changePage(page)} />
      ))}

      {size - (MAX_OFFSET + 1) > currentPage && <Ellipsis />}

      <PageButton page={size} active={size === currentPage} onClick={() => changePage(size)} />

      <IconButton aria-label="Następna strona" icon={<FiChevronRight />} isDisabled={currentPage === size} onClick={() => changePage(currentPage + 1)} {...arrowProps} />
    </Flex>
  );
};
